import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import XLSX from 'xlsx-js-style';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';

interface Project {
  id: number;
  title: string;
  description?: string;
  customer_name: string;
  status: 'planning' | 'delivery_ready' | 'installation' | 'remedial' | 'remedial_resolved' | 'completed';
  budget: string | number;
  material_cost?: string | number;
  actual_cost: string | number;
  start_date: string;
  end_date: string;
  delivery_date?: string;
  assigned_user: string | null;
  lead_id?: number;
  customer_id: number;
  order_number?: string;
  created_at: string;
  updated_at: string;
  remedial_additional_fitter_cost?: number;
  remedial_additional_delivery_cost?: number;
  remedial_additional_material_cost?: number;
  remedial_count?: number;
  open_remedial_count?: number;
}

export default function Remedials() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedMonth, setSelectedMonth] = useState<number>(0); // All months
  const [selectedYear, setSelectedYear] = useState<number>(0); // All years

  // Legacy customer modal state
  const [legacyModalOpen, setLegacyModalOpen] = useState(false);
  const [legacySubmitting, setLegacySubmitting] = useState(false);
  const [legacyForm, setLegacyForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    postal_code: '',
    project_title: '',
    project_notes: '',
    original_work_date: '',
    order_number: '',
  });

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const cacheBuster = `${Date.now()}_${Math.random()}`;
      const response = await fetch(`/api/projects?limit=1000&_t=${cacheBuster}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
        },
        cache: 'no-store',
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      } else {
        console.error('Failed to load projects:', response.statusText);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter projects by selected month and year
  const getMonthProjects = () => {
    // If both are "All", return everything
    if (selectedMonth === 0 && selectedYear === 0) {
      return projects;
    }

    return projects.filter(project => {
      const projectDate = new Date(project.created_at);
      const projectMonth = projectDate.getMonth();
      const projectYear = projectDate.getFullYear();

      // Check month (if specific month selected)
      const matchesMonth = selectedMonth === 0 || projectMonth === (selectedMonth - 1);

      // Check year (if specific year selected)
      const matchesYear = selectedYear === 0 || projectYear === selectedYear;

      return matchesMonth && matchesYear;
    });
  };

  const monthProjects = getMonthProjects();

  const filteredProjects = monthProjects.filter(project => {
    const matchesSearch =
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.order_number && project.order_number.toLowerCase().includes(searchTerm.toLowerCase()));

    // Filter by remedial status
    let matchesStatus = true;
    if (statusFilter === 'remedial') {
      matchesStatus = project.status === 'remedial';
    } else if (statusFilter === 'remedial_resolved') {
      matchesStatus = project.status === 'remedial_resolved';
    } else if (statusFilter === 'all') {
      matchesStatus = project.status === 'remedial' || project.status === 'remedial_resolved';
    }

    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    if (!sortField) return 0;
    
    let aVal: any = '';
    let bVal: any = '';
    
    switch (sortField) {
      case 'title':
        aVal = a.title.toLowerCase();
        bVal = b.title.toLowerCase();
        break;
      case 'customer_name':
        aVal = a.customer_name.toLowerCase();
        bVal = b.customer_name.toLowerCase();
        break;
      case 'status':
        aVal = a.status;
        bVal = b.status;
        break;
      case 'budget':
        aVal = parseFloat(a.budget.toString());
        bVal = parseFloat(b.budget.toString());
        break;
      case 'start_date':
        aVal = new Date(a.start_date).getTime();
        bVal = new Date(b.start_date).getTime();
        break;
      case 'assigned_user':
        aVal = (a.assigned_user || '').toLowerCase();
        bVal = (b.assigned_user || '').toLowerCase();
        break;
      default:
        return 0;
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: { backgroundColor: string; color: string } } = {
      planning: { backgroundColor: '#ff9800', color: '#fff' },
      delivery_ready: { backgroundColor: '#3498DB', color: '#fff' },
      installation: { backgroundColor: '#9c27b0', color: '#fff' },
      remedial: { backgroundColor: '#f44336', color: '#fff' },
      remedial_resolved: { backgroundColor: '#4caf50', color: '#fff' },
      completed: { backgroundColor: '#4caf50', color: '#fff' },
    };
    return colors[status] || { backgroundColor: '#9e9e9e', color: '#fff' };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const getBudgetStatus = (budget: string | number, actual: string | number) => {
    const budgetNum = parseFloat(budget.toString());
    const actualNum = parseFloat(actual.toString());
    const percentage = (actualNum / budgetNum) * 100;
    if (percentage > 100) return 'Over Budget';
    if (percentage > 90) return 'Near Budget';
    return 'On Budget';
  };

  const handleProjectClick = (projectId: number) => {
    navigate(`/projects/${projectId}`);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleLegacyFormChange = (field: string, value: string) => {
    setLegacyForm(prev => ({ ...prev, [field]: value }));
  };

  const handleLegacySubmit = async () => {
    if (!legacyForm.first_name || !legacyForm.last_name) {
      alert('First name and last name are required');
      return;
    }

    setLegacySubmitting(true);
    try {
      const token = localStorage.getItem('token');

      // Geocode postcode to get coordinates
      let latitude = null;
      let longitude = null;
      if (legacyForm.postal_code) {
        try {
          const cleanPostcode = legacyForm.postal_code.replace(/\s+/g, '').toUpperCase();
          const geocodeResponse = await fetch(`https://api.postcodes.io/postcodes/${cleanPostcode}`);
          const geocodeData = await geocodeResponse.json();
          if (geocodeData.status === 200 && geocodeData.result) {
            latitude = geocodeData.result.latitude;
            longitude = geocodeData.result.longitude;
          }
        } catch (geocodeError) {
          console.error('Error geocoding postcode:', geocodeError);
        }
      }

      // First create the customer
      const customerResponse = await fetch('/api/customers', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: legacyForm.first_name,
          last_name: legacyForm.last_name,
          phone: legacyForm.phone,
          email: legacyForm.email,
          address: legacyForm.address,
          city: legacyForm.city,
          postal_code: legacyForm.postal_code,
          latitude: latitude,
          longitude: longitude,
          notes: 'Legacy customer - added via Remedials page',
        }),
      });

      if (!customerResponse.ok) {
        throw new Error('Failed to create customer');
      }

      const customerData = await customerResponse.json();
      const customerId = customerData.id;

      // Then create the project with remedial status
      const projectTitle = legacyForm.project_title || `${legacyForm.first_name} ${legacyForm.last_name} - Legacy Project`;
      const projectResponse = await fetch('/api/projects', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: projectTitle,
          description: legacyForm.project_notes,
          customer_id: customerId,
          status: 'remedial',
          start_date: legacyForm.original_work_date || null,
          end_date: legacyForm.original_work_date || null,
          order_number: legacyForm.order_number || null,
        }),
      });

      if (!projectResponse.ok) {
        throw new Error('Failed to create project');
      }

      const projectData = await projectResponse.json();

      // Reset form and close modal
      setLegacyForm({
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        postal_code: '',
        project_title: '',
        project_notes: '',
        original_work_date: '',
        order_number: '',
      });
      setLegacyModalOpen(false);

      // Refresh projects list
      loadProjects();

      // Navigate to the new project
      navigate(`/projects/${projectData.id}`);

    } catch (error) {
      console.error('Error creating legacy customer:', error);
      alert('Failed to create legacy customer. Please try again.');
    } finally {
      setLegacySubmitting(false);
    }
  };

  const handleExportToExcel = async () => {
    try {
      const token = localStorage.getItem('token');

      // Filter projects with remedials
      const remedialProjects = projects.filter(project =>
        project.status === 'remedial' || project.status === 'remedial_resolved'
      );

      console.log('Remedial projects found:', remedialProjects.length);

      // Fetch all remedial issues for these projects
      const allRemedialsData = await Promise.all(
        remedialProjects.map(async (project) => {
          try {
            // Fetch project details with customer info
            const projectResponse = await fetch(`/api/projects/${project.id}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });
            const projectResponse_json = await projectResponse.json();
            const projectData = projectResponse_json.project || projectResponse_json;

            // Fetch remedial issues for this project
            const remedialsResponse = await fetch(`/api/projects/${project.id}/remedials`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });
            const remedialsData = await remedialsResponse.json();
            const remedials = remedialsData.remedials || [];

            console.log(`Project ${project.id} has ${remedials.length} remedials`);

            // Check if remedials is an array
            if (!Array.isArray(remedials)) {
              console.warn(`Remedials for project ${project.id} is not an array:`, remedials);
              return [];
            }

            // Map each remedial issue to the export format
            return remedials.map((remedial: any) => ({
              project: projectData,
              remedial: remedial,
            }));
          } catch (error) {
            console.error(`Error fetching data for project ${project.id}:`, error);
            return [];
          }
        })
      );

      // Flatten the array
      const flattenedData = allRemedialsData.flat();
      console.log('Total remedial issues:', flattenedData.length);
      console.log('Flattened data sample:', flattenedData[0]);

      // Separate open and completed remedials
      const openRemedialsData = flattenedData
        .filter(item => item.remedial.status === 'open')
        .map(item => ({
          'Name': item.project.customer_name || '',
          'Address': [
            item.project.customer_address_line_1,
            item.project.customer_address_2,
            item.project.customer_city,
            item.project.customer_postcode
          ].filter(Boolean).join(', '),
          'Repair': item.remedial.repair || '',
          'Number': item.project.customer_phone || '',
          'Ref': item.project.order_number || '',
          'Further Info': item.remedial.description || '',
          'Fit Date': item.remedial.start_date ? new Date(item.remedial.start_date).toLocaleDateString('en-GB') : '',
          'Location of Materials': item.remedial.location_of_materials || ''
        }));

      const completedRemedialsData = flattenedData
        .filter(item => item.remedial.status === 'resolved')
        .map(item => {
          const fitterCost = parseFloat(item.remedial.fitter_cost) || 0;
          const deliveryCost = parseFloat(item.remedial.delivery_cost) || 0;
          const materialCost = parseFloat(item.remedial.material_cost) || 0;
          const totalCost = fitterCost + deliveryCost + materialCost;

          return {
            'Name': item.project.customer_name || '',
            'Address': [
              item.project.customer_address_line_1,
              item.project.customer_address_2,
              item.project.customer_city,
              item.project.customer_postcode
            ].filter(Boolean).join(', '),
            'Repair': item.remedial.repair || '',
            'Number': item.project.customer_phone || '',
            'Ref': item.project.order_number || '',
            'Further Info': item.remedial.description || '',
            'Fit Date': item.remedial.start_date ? new Date(item.remedial.start_date).toLocaleDateString('en-GB') : '',
            'Location of Materials': item.remedial.location_of_materials || '',
            'Date Fitter Invoice Paid': item.remedial.date_invoice_paid ? new Date(item.remedial.date_invoice_paid).toLocaleDateString('en-GB') : '',
            'Date Materials Invoice Paid': item.remedial.date_materials_invoice_paid ? new Date(item.remedial.date_materials_invoice_paid).toLocaleDateString('en-GB') : '',
            'Fitter Cost': fitterCost > 0 ? `£${fitterCost.toFixed(2)}` : '',
            'Delivery Cost': deliveryCost > 0 ? `£${deliveryCost.toFixed(2)}` : '',
            'Materials Cost': materialCost > 0 ? `£${materialCost.toFixed(2)}` : '',
            'Total Cost': totalCost > 0 ? `£${totalCost.toFixed(2)}` : '',
            "Who's At Fault?": item.remedial.whos_at_fault || ''
          };
        });

      console.log('Open remedials:', openRemedialsData.length);
      console.log('Completed remedials:', completedRemedialsData.length);

      // Create workbook
      const wb = XLSX.utils.book_new();

      // Create worksheets
      const wsOpen = XLSX.utils.json_to_sheet(openRemedialsData);
      const wsCompleted = XLSX.utils.json_to_sheet(completedRemedialsData);

      // Set column widths and text wrapping for Open Remedials sheet
      wsOpen['!cols'] = [
        { wch: 20 }, // Name
        { wch: 30 }, // Address
        { wch: 30 }, // Repair
        { wch: 15 }, // Number
        { wch: 15 }, // Ref
        { wch: 40 }, // Further Info
        { wch: 12 }, // Fit Date
        { wch: 25 }  // Location of Materials
      ];

      // Apply text wrapping to all cells in Open Remedials
      const rangeOpen = XLSX.utils.decode_range(wsOpen['!ref'] || 'A1');
      for (let R = rangeOpen.s.r; R <= rangeOpen.e.r; ++R) {
        for (let C = rangeOpen.s.c; C <= rangeOpen.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (!wsOpen[cellAddress]) continue;
          if (!wsOpen[cellAddress].s) wsOpen[cellAddress].s = {};
          wsOpen[cellAddress].s.alignment = { wrapText: true, vertical: 'top' };
        }
      }

      // Set column widths and text wrapping for Completed Remedials sheet
      wsCompleted['!cols'] = [
        { wch: 20 }, // Name
        { wch: 30 }, // Address
        { wch: 30 }, // Repair
        { wch: 15 }, // Number
        { wch: 15 }, // Ref
        { wch: 40 }, // Further Info
        { wch: 12 }, // Fit Date
        { wch: 25 }, // Location of Materials
        { wch: 15 }, // Date Fitter Invoice Paid
        { wch: 15 }, // Date Materials Invoice Paid
        { wch: 12 }, // Fitter Cost
        { wch: 12 }, // Delivery Cost
        { wch: 14 }, // Materials Cost
        { wch: 12 }, // Total Cost
        { wch: 18 }  // Who's At Fault?
      ];

      // Apply text wrapping to all cells in Completed Remedials
      const rangeCompleted = XLSX.utils.decode_range(wsCompleted['!ref'] || 'A1');
      for (let R = rangeCompleted.s.r; R <= rangeCompleted.e.r; ++R) {
        for (let C = rangeCompleted.s.c; C <= rangeCompleted.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (!wsCompleted[cellAddress]) continue;
          if (!wsCompleted[cellAddress].s) wsCompleted[cellAddress].s = {};
          wsCompleted[cellAddress].s.alignment = { wrapText: true, vertical: 'top' };
        }
      }

      // Add worksheets to workbook
      XLSX.utils.book_append_sheet(wb, wsOpen, 'Open Remedials');
      XLSX.utils.book_append_sheet(wb, wsCompleted, 'Completed Remedials');

      // Generate filename with current date
      const date = new Date().toISOString().split('T')[0];
      const filename = `Remedial_Issues_${date}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      alert(`Export complete! Open: ${openRemedialsData.length}, Completed: ${completedRemedialsData.length}`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Error exporting data. Please try again.');
    }
  };

  return (
    <Box>
      <Box sx={{
        mb: 3,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'flex-end',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2
      }}>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' } }}>
          <FormControl size="small" sx={{ minWidth: 120, width: { xs: '100%', sm: 'auto' } }}>
            <Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              displayEmpty
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)'
                }
              }}
            >
              <MenuItem value={0}>All Months</MenuItem>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                <MenuItem key={month} value={month}>
                  {new Date(2000, month - 1, 1).toLocaleDateString('en-US', { month: 'long' })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100, width: { xs: '100%', sm: 'auto' } }}>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              displayEmpty
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)'
                }
              }}
            >
              <MenuItem value={0}>All Years</MenuItem>
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleExportToExcel}
            sx={{ height: 40 }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            onClick={() => setLegacyModalOpen(true)}
            sx={{ height: 40 }}
          >
            Add Legacy Customer
          </Button>
        </Box>
      </Box>

      {/* Status Summary Boxes */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(3, 1fr)',
          md: 'repeat(5, 1fr)'
        },
        gap: { xs: 1, sm: 1, md: 1 },
        mb: 2.5
      }}>
        {/* Current Remedials */}
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
          onClick={() => setStatusFilter('remedial')}
        >
          <CardContent sx={{ p: { xs: 0.75, sm: 1, md: 1 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' } }}>
              Current Remedials
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' }, color: '#f44336' }}>
              {monthProjects.filter(p => p.status === 'remedial').length}
            </Typography>
          </CardContent>
        </Card>

        {/* Remedial Resolved */}
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
          onClick={() => setStatusFilter('remedial_resolved')}
        >
          <CardContent sx={{ p: { xs: 0.75, sm: 1, md: 1 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' } }}>
              Remedial Resolved
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' }, color: '#4caf50' }}>
              {monthProjects.filter(p => p.status === 'remedial_resolved').length}
            </Typography>
          </CardContent>
        </Card>

        {/* Total */}
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
          onClick={() => setStatusFilter('all')}
        >
          <CardContent sx={{ p: { xs: 0.75, sm: 1, md: 1 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' } }}>
              Total
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' }, color: '#1976d2' }}>
              {monthProjects.filter(p => p.status === 'remedial' || p.status === 'remedial_resolved').length}
            </Typography>
          </CardContent>
        </Card>

        {/* Cost of Outstanding */}
        <Card
          sx={{
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
        >
          <CardContent sx={{ p: { xs: 0.75, sm: 1, md: 1 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' } }}>
              Cost of Outstanding
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' }, color: '#f44336' }}>
              {formatCurrency(
                monthProjects
                  .filter(p => p.status === 'remedial')
                  .reduce((sum, p) => sum +
                    (Number(p.remedial_additional_fitter_cost) || 0) +
                    (Number(p.remedial_additional_delivery_cost) || 0) +
                    (Number(p.remedial_additional_material_cost) || 0), 0)
              )}
            </Typography>
          </CardContent>
        </Card>

        {/* Overall Costs */}
        <Card
          sx={{
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
        >
          <CardContent sx={{ p: { xs: 0.75, sm: 1, md: 1 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' } }}>
              Overall Costs
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' }, color: '#9c27b0' }}>
              {formatCurrency(
                monthProjects
                  .filter(p => p.status === 'remedial' || p.status === 'remedial_resolved')
                  .reduce((sum, p) => sum +
                    (Number(p.remedial_additional_fitter_cost) || 0) +
                    (Number(p.remedial_additional_delivery_cost) || 0) +
                    (Number(p.remedial_additional_material_cost) || 0), 0)
              )}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search Box - Mobile Only at Top */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search remedial projects..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Filters Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: { md: 'center' }
          }}>
            {/* Search Box - Desktop Only */}
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' }, display: { xs: 'none', md: 'block' } }}>
              <TextField
                fullWidth
                placeholder="Search remedial projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 25%' } }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="remedial">Remedial</MenuItem>
                  <MenuItem value="remedial_resolved">Remedial Resolved</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 25%' }, display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                {filteredProjects.length} projects found
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: { xs: 600, sm: 750, md: 900 } }}>
          <TableHead>
            <TableRow>
              <TableCell 
                onClick={() => handleSort('order_number')} 
                sx={{ 
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Order Number
                  {sortField === 'order_number' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                onClick={() => handleSort('title')} 
                sx={{ 
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Project
                  {sortField === 'title' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                onClick={() => handleSort('customer_name')} 
                sx={{ 
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                  display: { xs: 'none', sm: 'table-cell' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Customer
                  {sortField === 'customer_name' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell
                onClick={() => handleSort('status')}
                sx={{
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Status
                  {sortField === 'status' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                  display: { xs: 'none', sm: 'table-cell' }
                }}
              >
                Issues
              </TableCell>
              <TableCell
                onClick={() => handleSort('material_cost')}
                sx={{
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                  display: 'none'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Project Cost
                  {sortField === 'material_cost' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                  display: { xs: 'none', md: 'table-cell' }
                }}
              >
                Remedial Cost
              </TableCell>
              <TableCell
                onClick={() => handleSort('start_date')}
                sx={{
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                  display: 'none'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Dates
                  {sortField === 'start_date' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              </TableRow>
          </TableHead>
          <TableBody>
            {filteredProjects
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((project) => (
              <TableRow 
                key={project.id}
                onClick={() => handleProjectClick(project.id)}
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                <TableCell>
                  <Typography variant="body2">
                    {project.order_number || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {project.title}
                  </Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{project.customer_name}</TableCell>
                <TableCell>
                  <Chip
                    label={project.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    size="small"
                    sx={getStatusColor(project.status)}
                  />
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    {project.remedial_count || 0} total
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: (project.open_remedial_count || 0) > 0 ? '#f44336' : '#4caf50' }}>
                    {project.open_remedial_count || 0} open
                  </Typography>
                </TableCell>
                <TableCell sx={{ display: 'none' }}>
                  <Typography variant="body2">
                    {formatCurrency(project.material_cost ? parseFloat(project.material_cost.toString()) : 0)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Typography variant="body2">
                    {formatCurrency(
                      (Number(project.remedial_additional_fitter_cost) || 0) +
                      (Number(project.remedial_additional_delivery_cost) || 0) +
                      (Number(project.remedial_additional_material_cost) || 0)
                    )}
                  </Typography>
                </TableCell>
                <TableCell sx={{ display: 'none' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    <strong>Start:</strong> {project.start_date ? new Date(project.start_date).toLocaleDateString('en-GB') : 'Not set'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    <strong>End:</strong> {project.end_date ? new Date(project.end_date).toLocaleDateString('en-GB') : 'Not set'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    <strong>Delivery:</strong> {project.delivery_date ? new Date(project.delivery_date).toLocaleDateString('en-GB') : 'Not set'}
                  </Typography>
                </TableCell>
                </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredProjects.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Add Legacy Customer Modal */}
      <Dialog open={legacyModalOpen} onClose={() => setLegacyModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Legacy Customer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Add a customer who had work done before the CRM system was set up.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="First Name"
                value={legacyForm.first_name}
                onChange={(e) => handleLegacyFormChange('first_name', e.target.value)}
                fullWidth
                required
                size="small"
              />
              <TextField
                label="Last Name"
                value={legacyForm.last_name}
                onChange={(e) => handleLegacyFormChange('last_name', e.target.value)}
                fullWidth
                required
                size="small"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Phone"
                value={legacyForm.phone}
                onChange={(e) => handleLegacyFormChange('phone', e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Email"
                value={legacyForm.email}
                onChange={(e) => handleLegacyFormChange('email', e.target.value)}
                fullWidth
                size="small"
              />
            </Box>
            <TextField
              label="Address"
              value={legacyForm.address}
              onChange={(e) => handleLegacyFormChange('address', e.target.value)}
              fullWidth
              size="small"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Town/City"
                value={legacyForm.city}
                onChange={(e) => handleLegacyFormChange('city', e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Postcode"
                value={legacyForm.postal_code}
                onChange={(e) => handleLegacyFormChange('postal_code', e.target.value)}
                fullWidth
                size="small"
              />
            </Box>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Project Details</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Order Number"
                value={legacyForm.order_number}
                onChange={(e) => handleLegacyFormChange('order_number', e.target.value)}
                size="small"
                sx={{ flex: 1 }}
              />
              <TextField
                label="Project Title (optional)"
                value={legacyForm.project_title}
                onChange={(e) => handleLegacyFormChange('project_title', e.target.value)}
                fullWidth
                size="small"
                placeholder="Leave blank for auto-generated title"
                sx={{ flex: 2 }}
              />
              <TextField
                label="Original Work Date"
                type="date"
                value={legacyForm.original_work_date}
                onChange={(e) => handleLegacyFormChange('original_work_date', e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>
            <TextField
              label="Notes about original work"
              value={legacyForm.project_notes}
              onChange={(e) => handleLegacyFormChange('project_notes', e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLegacyModalOpen(false)} disabled={legacySubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLegacySubmit}
            disabled={legacySubmitting || !legacyForm.first_name || !legacyForm.last_name}
          >
            {legacySubmitting ? <CircularProgress size={24} /> : 'Create Customer & Project'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}